import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase, POSTS_TABLE } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import Seo from '../../components/Seo';
import {
  Plus, Search, Edit3, Trash2, Eye, EyeOff, LogOut,
  FileText, CheckCircle2, ExternalLink, ArrowRight,
} from 'lucide-react';

export default function AdminDashboard() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | published | draft
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    const { data, error } = await supabase
      .from(POSTS_TABLE)
      .select('id, title, slug, excerpt, cover_url, status, published_at, updated_at, tags, author_email')
      .order('updated_at', { ascending: false });
    if (error) {
      setError(error.message);
      setPosts([]);
    } else {
      setPosts(data || []);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return posts.filter((p) => {
      const s = (p.status || 'draft').toLowerCase();
      if (filter !== 'all' && s !== filter) return false;
      if (!query) return true;
      const hay = `${p.title || ''} ${p.excerpt || ''} ${(p.tags || []).join(' ')} ${p.slug || ''}`.toLowerCase();
      return hay.includes(query);
    });
  }, [posts, q, filter]);

  const togglePublish = async (post) => {
    const nextStatus = post.status === 'published' ? 'draft' : 'published';
    const patch = { status: nextStatus };
    if (nextStatus === 'published' && !post.published_at) patch.published_at = new Date().toISOString();
    const { error } = await supabase.from(POSTS_TABLE).update(patch).eq('id', post.id);
    if (error) { setError(error.message); return; }
    load();
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title || 'Untitled'}" — this cannot be undone.`)) return;
    const { error } = await supabase.from(POSTS_TABLE).delete().eq('id', post.id);
    if (error) { setError(error.message); return; }
    load();
  };

  const doSignOut = async () => {
    await signOut();
    navigate('/admin/login', { replace: true });
  };

  const counts = useMemo(() => ({
    all: posts.length,
    published: posts.filter((p) => p.status === 'published').length,
    draft: posts.filter((p) => (p.status || 'draft') === 'draft').length,
  }), [posts]);

  return (
    <>
      <Seo title="Admin — Blog" description="Manage blog posts." path="/admin" />
      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
            <div>
              <div className="eyebrow mb-2">Blog Admin</div>
              <h1 className="font-serif-display text-3xl md:text-4xl text-[color:var(--deep-sage)]">Manage your posts</h1>
              <p className="text-sm text-[color:var(--taupe-gray)] mt-2">Signed in as <span className="font-semibold text-[color:var(--deep-sage)]">{user?.email}</span></p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/blog" target="_blank" rel="noreferrer" className="btn-secondary text-sm">
                <ExternalLink className="w-4 h-4" /> View public blog
              </Link>
              <Link to="/admin/posts/new" className="btn-primary text-sm">
                <Plus className="w-4 h-4" /> New post
              </Link>
              <button onClick={doSignOut} className="text-sm text-[color:var(--taupe-gray)] hover:text-[color:var(--dusty-rose)] flex items-center gap-1.5 px-3 py-2">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <button onClick={() => setFilter('all')} className={`card-soft p-5 text-left transition ${filter === 'all' ? 'ring-2 ring-[color:var(--dusty-rose)]' : ''}`}>
              <div className="flex items-center gap-2 text-[color:var(--taupe-gray)]"><FileText className="w-4 h-4" /> <span className="text-xs uppercase tracking-wider">All posts</span></div>
              <div className="font-serif-display text-3xl text-[color:var(--deep-sage)] mt-1">{counts.all}</div>
            </button>
            <button onClick={() => setFilter('published')} className={`card-soft p-5 text-left transition ${filter === 'published' ? 'ring-2 ring-[color:var(--sage-dark)]' : ''}`}>
              <div className="flex items-center gap-2 text-[color:var(--taupe-gray)]"><CheckCircle2 className="w-4 h-4" /> <span className="text-xs uppercase tracking-wider">Published</span></div>
              <div className="font-serif-display text-3xl text-[color:var(--sage-dark)] mt-1">{counts.published}</div>
            </button>
            <button onClick={() => setFilter('draft')} className={`card-soft p-5 text-left transition ${filter === 'draft' ? 'ring-2 ring-[color:var(--dusty-rose)]' : ''}`}>
              <div className="flex items-center gap-2 text-[color:var(--taupe-gray)]"><EyeOff className="w-4 h-4" /> <span className="text-xs uppercase tracking-wider">Drafts</span></div>
              <div className="font-serif-display text-3xl text-[color:var(--dusty-rose)] mt-1">{counts.draft}</div>
            </button>
          </div>

          {/* Search */}
          <div className="card-soft p-3 mb-4 flex items-center gap-2">
            <Search className="w-4 h-4 text-[color:var(--taupe-gray)] ml-2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, tag, or excerpt…"
              className="flex-1 px-2 py-2 text-sm bg-transparent focus:outline-none"
            />
          </div>

          {error && (
            <div className="rounded-xl px-4 py-3 mb-4 text-sm" style={{ background: 'rgba(232,165,169,0.12)', color: '#8B3A44' }}>
              {error}
              <div className="text-xs mt-1 opacity-80">If this is your first time using the admin, make sure the SQL setup has been run in Supabase (see setup instructions).</div>
            </div>
          )}

          {/* Posts table */}
          <div className="card-soft overflow-hidden">
            <div className="hidden md:grid grid-cols-[1fr_120px_140px_120px] gap-3 px-5 py-3 text-[10px] uppercase tracking-[0.2em] font-bold text-[color:var(--sage-dark)] border-b" style={{ borderColor: 'var(--soft-border)', background: 'rgba(184,190,164,0.08)' }}>
              <div>Post</div>
              <div>Status</div>
              <div>Updated</div>
              <div className="text-right">Actions</div>
            </div>

            {loading ? (
              <div className="p-10 text-center text-[color:var(--taupe-gray)]">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center">
                <div className="font-serif-display text-xl text-[color:var(--deep-sage)] mb-2">No posts yet</div>
                <p className="text-sm text-[color:var(--taupe-gray)] mb-4">Create your first blog post to get started.</p>
                <Link to="/admin/posts/new" className="btn-primary text-sm inline-flex"><Plus className="w-4 h-4" /> New post <ArrowRight className="w-4 h-4" /></Link>
              </div>
            ) : (
              filtered.map((p) => (
                <div key={p.id} className="grid md:grid-cols-[1fr_120px_140px_120px] gap-3 items-center px-5 py-4 border-b last:border-0" style={{ borderColor: 'var(--soft-border)' }}>
                  <div className="flex items-start gap-3 min-w-0">
                    {p.cover_url ? (
                      <img src={p.cover_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(232,165,169,0.14)' }}>
                        <FileText className="w-5 h-5 text-[color:var(--dusty-rose)]" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <Link to={`/admin/posts/${p.id}/edit`} className="font-serif-display text-base text-[color:var(--deep-sage)] hover:text-[color:var(--dusty-rose)] transition line-clamp-1">
                        {p.title || 'Untitled'}
                      </Link>
                      <div className="text-xs text-[color:var(--taupe-gray)] line-clamp-1">/{p.slug || '—'}</div>
                    </div>
                  </div>
                  <div>
                    {p.status === 'published' ? (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-semibold" style={{ background: 'rgba(184,190,164,0.28)', color: 'var(--sage-dark)' }}>Published</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-semibold" style={{ background: 'rgba(232,165,169,0.20)', color: 'var(--dusty-rose)' }}>Draft</span>
                    )}
                  </div>
                  <div className="text-xs text-[color:var(--taupe-gray)]">
                    {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : '—'}
                  </div>
                  <div className="flex justify-end gap-1">
                    <button onClick={() => togglePublish(p)} title={p.status === 'published' ? 'Unpublish' : 'Publish'} className="w-9 h-9 rounded-lg hover:bg-[color:var(--cream)] flex items-center justify-center text-[color:var(--sage-dark)] transition">
                      {p.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <Link to={`/admin/posts/${p.id}/edit`} title="Edit" className="w-9 h-9 rounded-lg hover:bg-[color:var(--cream)] flex items-center justify-center text-[color:var(--deep-sage)] transition">
                      <Edit3 className="w-4 h-4" />
                    </Link>
                    <button onClick={() => remove(p)} title="Delete" className="w-9 h-9 rounded-lg hover:bg-[color:var(--cream)] flex items-center justify-center text-[color:var(--dusty-rose)] transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </>
  );
}
